export const membershipContent = {
  hero: {
    title: "Pricing",
    subtitle: "Build a ritual. Form a habit. Commit to your wellbeing. RE:SET memberships are designed for consistency, longevity, and sustainable transformation — not quick fixes."
  },
  philosophy: "At RE:SET, membership is not a subscription — it is a commitment to yourself. A recurring rhythm of movement, recovery, and restoration that becomes part of how you live. We believe in showing up, building momentum, and creating lasting change through consistent practice.",
  pilates: {
    title: "Reformer Pilates Memberships",
    subtitle: "Recurring Wellness",
    description: "Choose the membership that supports your rhythm. All tiers include access to our boutique 8-10 bed studio, expert instruction, and a calm, premium environment.",
    tiers: [
      {
        name: "Starter",
        price: 65,
        description: "Begin your practice with intention.",
        benefits: [
          "Consistency — Build a sustainable wellness rhythm",
          "Priority access — Book your preferred class times",
          "Better value — Save vs pay-as-you-go pricing"
        ],
        features: [
          "4 Reformer Pilates classes per month",
          "Roll over 2 unused classes",
          "Priority booking window",
          "Member-only workshops",
          "10% discount on café purchases",
          "Complimentary mat hire"
        ]
      },
      {
        name: "Momentum",
        price: 150,
        description: "Build consistency and rhythm.",
        benefits: [
          "Consistency — Establish a regular practice routine",
          "Priority access — Extended booking privileges",
          "Better value — Significant savings per class"
        ],
        features: [
          "10 Reformer Pilates classes per month",
          "Roll over 3 unused classes",
          "Extended booking priority",
          "Member-only workshops",
          "15% discount on café purchases",
          "Complimentary mat hire",
          "Guest pass (1 per month)"
        ]
      },
      {
        name: "Committed",
        price: 225,
        description: "Deepen your practice with dedication.",
        benefits: [
          "Consistency — Full immersion in your practice",
          "Priority access — First access to new class times",
          "Better value — Maximum savings per session"
        ],
        features: [
          "16 Reformer Pilates classes per month",
          "Roll over 4 unused classes",
          "First access to new class times",
          "Member-only workshops",
          "20% discount on café purchases",
          "Complimentary mat hire",
          "Guest passes (2 per month)",
          "Quarterly progress review"
        ]
      },
      {
        name: "Unlimited",
        price: 295,
        description: "Full immersion in your wellbeing.",
        benefits: [
          "Consistency — Unlimited practice for total commitment",
          "Priority access — First access to all bookings",
          "Better value — Best value for dedicated practitioners"
        ],
        features: [
          "Unlimited Reformer Pilates classes",
          "First access to all bookings",
          "Member-only workshops",
          "25% discount on café purchases",
          "Complimentary mat hire",
          "Guest passes (4 per month)",
          "Quarterly progress review",
          "Priority thermal suite booking"
        ]
      }
    ]
  },
  payg: {
    title: "Pay-As-You-Go & Credits",
    description: "Prefer flexibility? Book individual classes or purchase credit packages for better value.",
    options: [
      {
        name: "Single Class",
        price: "£20",
        description: "Drop in for a single reformer class",
        benefits: [
          "Flexibility — No commitment required",
          "Convenience — Book when it suits you",
          "No long-term commitment — Try before you commit"
        ]
      },
      {
        name: "3-Class Credit",
        price: "£80",
        description: "3 classes to use within 30 days",
        benefits: [
          "Flexibility — Use credits at your own pace",
          "Convenience — Better value than single classes",
          "No long-term commitment — Perfect for occasional visits"
        ],
        savings: "Save £20 vs single classes"
      }
    ]
  },
  thermal: {
    title: "Thermal Suite Access",
    description: "Add nervous system restoration to your practice. Our fully private thermal suites offer sauna, steam, and ice plunge — booked by the hour for complete privacy.",
    options: [
      {
        name: "Pay As You Go",
        price: "£60–£80",
        description: "Book individual private thermal sessions as needed.",
        benefits: [
          "Flexibility — Book sessions when you need them",
          "Convenience — No monthly commitment",
          "No long-term commitment — Perfect for occasional recovery"
        ],
        features: [
          "60-minute private suite access",
          "Sauna, steam, ice plunge",
          "Complete privacy",
          "Towels and amenities included",
          "Book online anytime"
        ]
      },
      {
        name: "Pilates Member Add-On",
        price: "£65 / month",
        description: "Add thermal recovery to your existing Pilates membership.",
        benefits: [
          "Consistency — Regular recovery sessions",
          "Priority access — Member booking privileges",
          "Better value — Discounted member rate"
        ],
        features: [
          "2 private thermal sessions per month",
          "60 minutes per session",
          "Priority booking access",
          "Towels and amenities included",
          "Additional sessions at member rate"
        ]
      },
      {
        name: "Wellness-Only Membership",
        price: "£65 / month",
        description: "Focus on recovery and nervous system restoration.",
        benefits: [
          "Consistency — Build a regular recovery ritual",
          "Priority access — Member booking privileges",
          "Better value — Save vs pay-as-you-go"
        ],
        features: [
          "2 private thermal sessions per month",
          "60 minutes per session",
          "Sauna, steam, ice plunge",
          "Towels and amenities included",
          "10% discount on café purchases"
        ]
      }
    ]
  },
  benefits: {
    title: "Why Membership?",
    subtitle: "RE:SET memberships are designed to support long-term wellbeing, not short-term goals.",
    items: [
      {
        icon: "ri-calendar-check-line",
        title: "Ritual Building",
        description: "Consistency creates transformation. Membership helps you build a sustainable rhythm of movement and recovery."
      },
      {
        icon: "ri-time-line",
        title: "Priority Access",
        description: "Members receive priority booking for classes and thermal suites, ensuring you can maintain your practice."
      },
      {
        icon: "ri-community-line",
        title: "Community",
        description: "Join a community of people committed to longevity, wellness, and showing up for themselves."
      },
      {
        icon: "ri-heart-pulse-line",
        title: "Longevity Focus",
        description: "Our approach is designed for sustainable health — not quick fixes or short-term results."
      },
      {
        icon: "ri-user-star-line",
        title: "Expert Guidance",
        description: "Work with experienced instructors who understand form, alignment, and long-term progression."
      },
      {
        icon: "ri-gift-line",
        title: "Member Perks",
        description: "Enjoy café discounts, guest passes, exclusive workshops, and priority access to new offerings."
      }
    ]
  },
  included: {
    title: "What's Included",
    items: [
      "Access to boutique 8-10 bed Reformer Pilates studio",
      "Expert instruction focused on form and alignment",
      "Calm, premium environment designed for focus",
      "Complimentary mat hire and equipment",
      "Member-only workshops and events",
      "Café discounts on wellness tonics and nourishment",
      "Priority booking for classes and thermal suites",
      "Guest passes to share the RE:SET experience",
      "Quarterly progress reviews (Committed & Unlimited)",
      "Online booking and class management",
      "Flexible class scheduling across morning and evening",
      "Roll-over credits for unused classes"
    ]
  },
  commitment: {
    title: "A Commitment, Not a Subscription",
    description: "RE:SET memberships require a minimum 3-month commitment. This is intentional. Real change takes time, consistency, and showing up — even when motivation wanes. We are here to support your long-term wellbeing, not short-term convenience.",
    points: [
      "All memberships renew monthly after the initial 3-month commitment.",
      "Cancel anytime after your initial commitment period with 30 days' notice.",
      "Pause your membership for up to 2 months per year (medical or travel reasons)."
    ]
  },
  cta: {
    title: "Ready to Begin?",
    subtitle: "Choose your membership and start building a sustainable practice of movement, recovery, and restoration.",
    button: "Explore Memberships"
  }
};
